import type { JSX, ReactNode } from 'react';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Resource } from '@frameio/graphql-client/generated';
import { useGetResourceLazyQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import AppUrlParams from '@frameio/shared/routing/params/params';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import type { AvatarGroupPillProps } from '@frameio/vapor';
import { MemberAccessDialog } from '../components/MemberAccessDialog';
import type { CustomMemberAccessDialogButtonProps } from '../components/MemberAccessDialog/MemberAccessDialogButton';
import { MemberAccessDialogButton } from '../components/MemberAccessDialog/MemberAccessDialogButton';

export type ExpandableView = 'members' | 'invites';

type Props = {
  canViewMembers?: boolean;
  ctaVariant?: AvatarGroupPillProps['variant'];
  hideSuggestedMembers?: boolean;
  isDialogShowingInitially?: boolean;
  label?: string;
  onResourceHasMutated?: (isCurrentResourceNotOriginalContext: boolean) => void;
  resourceId?: Resource['id'];
  showMembersInitially?: boolean;
  trackingSource?: TrackingSourceType;
  useInitialResourceId?: boolean;
  defaultExpandedView?: ExpandableView;
};

/**
 * This provides a button to initiate the member access flow and the member
 * dialog itself. A custom button can be provided in place of the default
 * button.
 */
export const useMemberAccessFlow = ({
  canViewMembers = true,
  ctaVariant = 'primary',
  hideSuggestedMembers = false,
  isDialogShowingInitially = false,
  label,
  onResourceHasMutated,
  showMembersInitially,
  resourceId: resourceIdProp,
  trackingSource = '',
  useInitialResourceId = true,
}: Props) => {
  const accountId = useActiveAccountId();
  const [expandedView, setExpandedView] = useState<ExpandableView>('invites');

  /* Open the members modal by default if this url param
   * is set ?members_dialog_open=true */
  const openMemberAccessDialogByDefault = use(AppUrlParams.members_dialog_open);

  useEffect(() => {
    if (openMemberAccessDialogByDefault) {
      setExpandedView('members');
    }
  }, [openMemberAccessDialogByDefault]);

  const [isMemberAccessDialogOpen, setIsMemberAccessDialogOpen] = useState(
    isDialogShowingInitially
  );

  const [clickedResourceId, setClickedResourceId] = useState<Resource['id']>();
  const resourceId = useInitialResourceId ? resourceIdProp : clickedResourceId;

  const [prefetchData, setPrefetchData] = useState(false);

  const dialogRef = useRef(null);
  const handleOnHover = useCallback(() => {
    setPrefetchData(true);
    dialogRef?.current.prefetch();
  }, []);

  const handleClick = useCallback(
    (
      view?: ExpandableView,
      newResourceId?: string,
      prefetchDataOnClick: boolean = false
    ) => {
      if (['members', 'invites'].includes(view)) {
        setExpandedView(view);
      } else {
        setExpandedView(showMembersInitially ? 'members' : 'invites');
      }
      if (!useInitialResourceId) {
        setClickedResourceId(newResourceId);
      }
      if (prefetchDataOnClick) {
        handleOnHover();
      }
      setIsMemberAccessDialogOpen(true);
    },
    [showMembersInitially, useInitialResourceId, handleOnHover]
  );

  const [fetchResource] = useGetResourceLazyQuery({
    variables: { accountId: accountId ?? '', id: resourceId ?? '' },
  });

  const handleResourceHasMutated = useCallback(
    (isCurrentResourceNotOriginalContext?: boolean) => {
      if (isCurrentResourceNotOriginalContext) {
        fetchResource();
      }
      onResourceHasMutated?.(isCurrentResourceNotOriginalContext);
    },
    [fetchResource, onResourceHasMutated]
  );

  useEffect(() => {
    if (useInitialResourceId) {
      setIsMemberAccessDialogOpen(isDialogShowingInitially);
    }
  }, [resourceId, useInitialResourceId, isDialogShowingInitially]);

  const result = useMemo(
    () => ({
      renderMemberAccessButton: (
        children?: (options: CustomMemberAccessDialogButtonProps) => ReactNode,
        avatarIcon?: JSX.Element
      ) => (
        <MemberAccessDialogButton
          avatarIcon={avatarIcon}
          ctaVariant={ctaVariant}
          label={label}
          onClick={handleClick}
          onHover={handleOnHover}
          resourceId={resourceId}
        >
          {children}
        </MemberAccessDialogButton>
      ),
      renderedMemberAccessDialog: (
        <MemberAccessDialog
          ref={dialogRef}
          canViewMembers={canViewMembers}
          hideSuggestedMembers={hideSuggestedMembers}
          isDialogShowing={isMemberAccessDialogOpen && Boolean(resourceId)}
          onRequestChange={setIsMemberAccessDialogOpen}
          onResourceHasMutated={handleResourceHasMutated}
          prefetchData={prefetchData}
          resourceId={resourceId}
          showMembersInitially={expandedView === 'members'}
          trackingSource={trackingSource}
        />
      ),
      handleClick,
      state: { isMemberAccessDialogOpen, handleResourceHasMutated },
    }),
    [
      canViewMembers,
      ctaVariant,
      expandedView,
      hideSuggestedMembers,
      handleClick,
      handleOnHover,
      handleResourceHasMutated,
      isMemberAccessDialogOpen,
      label,
      prefetchData,
      resourceId,
      trackingSource,
    ]
  );

  return result;
};
